import {Component, Input, OnDestroy, OnInit, ViewChild} from '@angular/core';
import {CommonModule, formatCurrency} from "@angular/common";
import {ButtonModule} from "primeng/button";
import {ProgressSpinnerModule} from "primeng/progressspinner";
import {Product} from "@app/core/interfaces";
import {DynamicDialogRef} from "primeng/dynamicdialog";
import {EbayService, ProductService, UtilityService} from "@app/core/services";
import {Subject, Subscription, takeUntil} from "rxjs";
import {
    ChartComponent,
    ApexAxisChartSeries,
    ApexChart,
    ApexTitleSubtitle,
    ApexXAxis,
    ApexDataLabels,
    ApexStroke,
    ApexYAxis,
    ApexMarkers,
    ApexFill,
    ApexLegend
} from "ng-apexcharts";
import {EbayProductChartAnalysis} from "@app/core/interfaces/ebay";

export type ChartOptions = {
    series: ApexAxisChartSeries;
    chart: ApexChart;
    dataLabels: ApexDataLabels;
    stroke: ApexStroke;
    markers: ApexMarkers;
    colors: string[];
    fill: ApexFill;
    legend: ApexLegend;
    tooltip: ApexTooltip;
};

@Component({
  selector: 'app-product-stock-chart',
    imports: [
        CommonModule,
        ButtonModule,
        ProgressSpinnerModule,
        ChartComponent,
    ],
  templateUrl: './product-stock-chart.html'
})
export class ProductStockChart implements OnDestroy, OnInit {
    @Input() product!: Product;
    @ViewChild("chart") chart!: ChartComponent;
    public chartOptions: ChartOptions;

    protected apiCall$?: Subscription;
    private destroy$: Subject<any> = new Subject();

    isLoadingData: boolean = true;

    constructor(
        private dialogRef: DynamicDialogRef,
        private ebayService: EbayService,
        public utilityService: UtilityService,
    ) {
        const testSeries: ApexAxisChartSeries = [
            {
                type: "rangeArea",
                name: "Occasion Range",

                data: [
                    {
                        x: "Jan",
                        y: [1100, 1900]
                    },
                    {
                        x: "Feb",
                        y: [1200, 1800]
                    },
                    {
                        x: "Mar",
                        y: [900, 2900]
                    },
                    {
                        x: "Apr",
                        y: [1400, 2700]
                    },
                    {
                        x: "May",
                        y: [2600, 3900]
                    },
                    {
                        x: "Jun",
                        y: [500, 1700]
                    },
                    {
                        x: "Jul",
                        y: [1900, 2300]
                    },
                    {
                        x: "Aug",
                        y: [1000, 1500]
                    }
                ]
            },

            {
                type: "rangeArea",
                name: "Neuf Range",
                data: [
                    {
                        x: "Jan",
                        y: [3100, 3400]
                    },
                    {
                        x: "Feb",
                        y: [4200, 5200]
                    },
                    {
                        x: "Mar",
                        y: [3900, 4900]
                    },
                    {
                        x: "Apr",
                        y: [3400, 3900]
                    },
                    {
                        x: "May",
                        y: [5100, 5900]
                    },
                    {
                        x: "Jun",
                        y: [5400, 6700]
                    },
                    {
                        x: "Jul",
                        y: [4300, 4600]
                    },
                    {
                        x: "Aug",
                        y: [2100, 2900]
                    }
                ]
            },

            {
                type: "line",
                name: "Occasion Median",
                data: [
                    {
                        x: "Jan",
                        y: 1500
                    },
                    {
                        x: "Feb",
                        y: 1700
                    },
                    {
                        x: "Mar",
                        y: 1900
                    },
                    {
                        x: "Apr",
                        y: 2200
                    },
                    {
                        x: "May",
                        y: 3000
                    },
                    {
                        x: "Jun",
                        y: 1000
                    },
                    {
                        x: "Jul",
                        y: 2100
                    },
                    {
                        x: "Aug",
                        y: 1200
                    }
                ]
            },
            {
                type: "line",
                name: "Neuf Median",
                data: [
                    {
                        x: "Jan",
                        y: 3300
                    },
                    {
                        x: "Feb",
                        y: 4900
                    },
                    {
                        x: "Mar",
                        y: 4300
                    },
                    {
                        x: "Apr",
                        y: 3700
                    },
                    {
                        x: "May",
                        y: 5500
                    },
                    {
                        x: "Jun",
                        y: 5900
                    },
                    {
                        x: "Jul",
                        y: 4500
                    },
                    {
                        x: "Aug",
                        y: 2400
                    }
                ]
            }
        ];
        this.chartOptions = {
            series: [],
            chart: {
                height: 350,
                type: "rangeArea",
                animations: {
                    speed: 500
                }
            },
            colors: ["#15803d", "#c2410c", "#15803d", "#c2410c"],
            dataLabels: {
                enabled: false
            },
            fill: {
                opacity: [0.24, 0.24, 1, 1]
            },
            stroke: {
                curve: "straight",
                width: [0, 0, 2, 2]
            },
            legend: {
                show: true,
                customLegendItems: ["Neuf", "Occasion"],
            },
            markers: {
                colors: ["transparent", "transparent", "#15803d", "#c2410c" ],
                strokeWidth: [0,0,2,2],
                hover: {
                    sizeOffset: 5
                }
            },
            tooltip: {
                shared: true, // Affiche toutes les séries ayant la même valeur X
                intersect: false, // Permet d'afficher le tooltip en survolant l'axe
                custom: function({series, seriesIndex, dataPointIndex, w}) {
                    // console.log(w.config.series[0]);
                    // console.log(series[2][dataPointIndex], w.config.series[0].data[dataPointIndex].y);
                    // console.log(series[3][dataPointIndex], w.config.series[1].data[dataPointIndex].y);
                    return `
                    <div class="arrow_box p-2">
                        <div><span class="series-name" style="color: #15803d">Neuf: </span></div>
                        <div class="text-surface-500! dark:text-surface-700!"><span>Median: </span><span>${formatCurrency(series[2][dataPointIndex], 'fr-FR', '€', 'EUR')}</span></div>
                        <div class="text-surface-500! dark:text-surface-700!"><span>Min: </span><span>${formatCurrency(w.config.series[0].data[dataPointIndex].y[0], 'fr-FR', '€', 'EUR')}</span> - <span class="category">Max: </span><span>${formatCurrency(w.config.series[0].data[dataPointIndex].y[1], 'fr-FR', '€', 'EUR')}</span></div>

                        <div class="pt-2"><span class="series-name" style="color: #c2410c">Occasion: </span></div>
                        <div class="text-surface-500! dark:text-surface-700!"><span>Median: </span><span>${formatCurrency(series[3][dataPointIndex], 'fr-FR', '€', 'EUR')}</span></div>
                        <div class="text-surface-500! dark:text-surface-700!"><span>Min: </span><span>${formatCurrency(w.config.series[1].data[dataPointIndex].y[0], 'fr-FR', '€', 'EUR')}</span> - <span class="category">Max: </span><span>${formatCurrency(w.config.series[1].data[dataPointIndex].y[1], 'fr-FR', '€', 'EUR')}</span></div>
                    </div>`;
                }
            }
        };
    }

    ngOnInit(): void {
        this.getChartData()
    }

    getChartData() {
        this.apiCall$?.unsubscribe();
        this.apiCall$ = this.ebayService
            .getEbayAnalyseMarcheChart(this.product.id)
            .pipe(takeUntil(this.destroy$))
            .subscribe({
                next: (data: EbayProductChartAnalysis[]) => {
                    this.generateChartSeries(data);
                    this.apiCall$?.unsubscribe();
                },
                error: (error) => {
                    this.apiCall$?.unsubscribe();
                    this.isLoadingData = false;
                }
            });
    }

    generateChartSeries(data: EbayProductChartAnalysis[]): void {
        const occasionRangeData: any[] = [];
        const neufRangeData: any[] = [];
        const occasionMedianData: any[] = [];
        const neufMedianData: any[] = [];

        data.forEach((point) => {
            if (point.id === 'occasion') {
                point.data.range.forEach((rangePoint) => {
                    occasionRangeData.push({
                        x: this.utilityService.formatDate(rangePoint.x as number),
                        y: rangePoint.y
                    });
                });
                point.data.line.forEach((linePoint) => {
                    occasionMedianData.push({
                        x: this.utilityService.formatDate(linePoint.x as number),
                        y: linePoint.y
                    });
                });
            }
            if (point.id === 'neuf') {
                point.data.range.forEach((rangePoint) => {
                    neufRangeData.push({
                        x: this.utilityService.formatDate(rangePoint.x as number),
                        y: rangePoint.y
                    });
                });
                point.data.line.forEach((linePoint) => {
                    neufMedianData.push({
                        x: this.utilityService.formatDate(linePoint.x as number),
                        y: linePoint.y
                    });
                });
            }
        });

        // console.log('Occasion', occasionRangeData, occasionMedianData);
        // console.log('Neuf', neufRangeData, neufMedianData);

        this.chartOptions.series = [
            {
                type: "rangeArea",
                name: "Neuf Range",
                data: neufRangeData
            },{
                type: "rangeArea",
                name: "Occasion Range",
                data: occasionRangeData
            },
            {
                type: "line",
                name: "Neuf Median",
                data: neufMedianData
            },
            {
                type: "line",
                name: "Occasion Median",
                data: occasionMedianData
            },
        ];
        this.isLoadingData = false;
    }

    closeDialog(): void {
        this.dialogRef.close();
    }

    ngOnDestroy(): void {
        if (this.destroy$) {
            this.destroy$.next(true);
            this.destroy$.complete();
        }
        if(this.apiCall$) {
            this.apiCall$.unsubscribe();
        }
    }
}
